// src/pdf/pdfShared.js
//
// Helper isomorfi (browser + Node) per la generazione dei PDF.
// ⚠️ Questo file NON deve importare 'fs', 'path' o usare Buffer/window
// direttamente: viene usato sia dal frontend che dal backend.

/**
 * Caratteri extra coperti da WinAnsi (CP1252) nella fascia 0x80-0x9F.
 * StandardFonts.Helvetica usa WinAnsi: tutto ciò che non rientra
 * nel set fa lanciare un errore a drawText.
 */
const WINANSI_EXTRA = '\u20AC\u201A\u0192\u201E\u2026\u2020\u2021\u02C6\u2030\u0160\u2039\u0152\u017D\u2018\u2019\u201C\u201D\u2022\u2013\u2014\u02DC\u2122\u0161\u203A\u0153\u017E\u0178';

/**
 * Rende una stringa sicura per il font WinAnsi.
 * I caratteri non codificabili (emoji, alfabeti non latini, ...) diventano '?'
 * invece di far fallire l'intera generazione del PDF.
 */
export function sanitizeText(value) {
  if (value === undefined || value === null) return '';

  return String(value)
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .replace(/\t/g, '    ')
    .replace(/[\s\S]/gu, (ch) => {
      if (ch === '\n') return ch;
      const code = ch.codePointAt(0);
      if (code >= 32 && code <= 126) return ch;   // ASCII stampabile
      if (code >= 160 && code <= 255) return ch;  // Latin-1 (à, è, °, ...)
      if (WINANSI_EXTRA.includes(ch)) return ch;
      return '?';
    });
}

/**
 * Converte il base64 di una data URL in Uint8Array, sia nel browser
 * (atob) che in Node (Buffer).
 */
export function base64ToBytes(base64) {
  if (typeof atob === 'function') {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
  }

  // eslint-disable-next-line no-undef
  return new Uint8Array(Buffer.from(base64, 'base64'));
}

/**
 * Normalizza una data in gg/mm/aaaa.
 * Accetta gg/mm/aaaa, aaaa-mm-gg o qualsiasi valore parseabile da Date.
 */
export function toItalianDateString(value) {
  if (!value) return '';

  const s = String(value).trim();

  // già gg/mm/aaaa
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) return s;

  // ISO yyyy-mm-dd
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const [y, m, d] = s.split('-');
    return `${d}/${m}/${y}`;
  }

  const d = new Date(value);
  if (!Number.isNaN(d.getTime())) {
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  }

  return s;
}

/**
 * Crea gli helper di disegno (testo, X, testo multiriga) su una pagina.
 */
export function createWriters(page, font, { fontSize = 10, color } = {}) {
  const textColor = color;

  const writeText = (text, x, y) => {
    if (!text && text !== 0) return;
    page.drawText(sanitizeText(text), {
      x,
      y,
      size: fontSize,
      font,
      color: textColor,
    });
  };

  const drawX = (x, y, size = 12) => {
    page.drawText('X', { x, y, size, font, color: textColor });
  };

  /**
   * Manda a capo il testo su piu righe.
   *
   * Con floorY il testo non scende mai sotto quella quota: il verbale ha
   * caselle basse e prima si scriveva all'infinito, finendo sopra il piede
   * del modulo. Se non ci sta si riduce il corpo (fino a minFontSize) e
   * solo se ancora non basta si taglia, segnalandolo con i puntini.
   */
  const writeMultiline = (text, x, y, options = {}) => {
    if (!text && text !== 0) return;

    const {
      maxWidth = 430,
      lineHeight = 11,
      floorY = null,
      minFontSize = 7,
    } = options;

    const clean = sanitizeText(text)
      .replace(/\n{2,}/g, '\n') // niente doppi a capo
      .trim();

    // spezza in righe per un dato corpo
    const aCapo = (size) => {
      const righe = [];

      clean.split('\n').forEach((paragrafo) => {
        const parole = paragrafo.split(/\s+/);
        let riga = '';

        parole.forEach((parola) => {
          const prova = riga ? `${riga} ${parola}` : parola;
          if (font.widthOfTextAtSize(prova, size) > maxWidth && riga) {
            righe.push(riga);
            riga = parola;
          } else {
            riga = prova;
          }
        });

        if (riga) righe.push(riga);
      });

      return righe;
    };

    // quante righe ci stanno prima del fondo
    const capienza = (leading) =>
      floorY === null ? Infinity : Math.floor((y - floorY) / leading) + 1;

    let size = fontSize;
    let leading = lineHeight;
    let righe = aCapo(size);

    // prima di tagliare, si prova a rimpicciolire
    while (righe.length > capienza(leading) && size > minFontSize) {
      size -= 1;
      leading = Math.max(size + 1, (lineHeight * size) / fontSize);
      righe = aCapo(size);
    }

    const massimo = capienza(leading);
    let tagliato = false;

    if (righe.length > massimo) {
      righe = righe.slice(0, Math.max(massimo, 1));
      tagliato = true;
    }

    righe.forEach((riga, i) => {
      const finale = tagliato && i === righe.length - 1 ? `${riga}...` : riga;
      page.drawText(finale, { x, y: y - i * leading, size, font, color: textColor });
    });
  };

  return { writeText, drawX, writeMultiline };
}

/**
 * Incolla la firma (data URL PNG/JPEG) sulla pagina.
 */
export async function embedSignature(pdfDoc, page, dataUrl, x, y, w, h) {
  if (!dataUrl) return;

  const base64 = String(dataUrl).split(',')[1];
  if (!base64) return;

  const bytes = base64ToBytes(base64);
  const isJpeg = /^data:image\/jpe?g/i.test(String(dataUrl));

  const image = isJpeg ? await pdfDoc.embedJpg(bytes) : await pdfDoc.embedPng(bytes);

  const scaled = image.scale(1);
  page.drawImage(image, {
    x,
    y,
    width: w || scaled.width,
    height: h || scaled.height,
  });
}
